import type { Tenant, TenantKycStatus, TenantsListResponse, TenantDeleteEligibility } from '../models';

function toKycStatus(v: unknown): TenantKycStatus {
  const s = String(v ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  if (s === 'completed') return 'completed';
  if (s === 'rejected') return 'rejected';
  if (s === 'pending') return 'pending';
  /** Legacy / alternate API strings */
  if (s === 'approved') return 'completed';
  if (s === 'in_review' || s === 'in-review') return 'pending';
  return 'pending';
}

function toOptionalNumber(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function extractDetails(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const d = raw.details ?? raw.Details;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    return d as Record<string, unknown>;
  }
  return {};
}

/** First non-empty string among candidates (root + nested `details`). */
function firstString(
  ...candidates: unknown[]
): string {
  for (const v of candidates) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

function fileNameFromUrl(url: string): string | undefined {
  try {
    const u = new URL(url);
    const seg = u.pathname.split('/').filter(Boolean).pop();
    return seg ? decodeURIComponent(seg) : undefined;
  } catch {
    return undefined;
  }
}

function logoUrlFromRaw(
  raw: Record<string, unknown>,
  details: Record<string, unknown>
): string | undefined {
  const fromRoot = raw.LogoUrl ?? raw.logoUrl;
  const fromDetails = details.logo ?? details.Logo;
  const v = fromRoot ?? fromDetails;
  if (v == null || v === '') return undefined;
  const s = String(v).trim();
  if (s === '') return undefined;
  if (s.startsWith('data:')) return undefined;
  return s;
}

function poaUrlFromDetails(details: Record<string, unknown>): string | undefined {
  const v = details.vollmachtUrl ?? details.VollmachtUrl;
  if (v == null || v === '') return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

/**
 * Maps portal-admin tenant DTOs to the UI `Tenant` shape.
 * Accepts flat fields, PascalCase, camelCase, and nested `details` (GET-by-id shape).
 */
export function mapTenantFromApi(raw: Record<string, unknown>): Tenant {
  const details = extractDetails(raw);

  const tenantId = String(
    raw.TenantId ??
      raw.tenantId ??
      raw.id ??
      raw.Id ??
      raw._id ??
      raw.tenant_id ??
      ''
  );

  const rootTenantName = firstString(raw.TenantName, raw.tenantName);
  const displayNameFromDetails = firstString(
    details.displayName,
    details.DisplayName
  );
  const tenantName = rootTenantName || displayNameFromDetails;

  const email = firstString(
    raw.Email,
    raw.email,
    raw.primaryEmail,
    details.primaryEmail,
    details.PrimaryEmail
  );

  const city = firstString(raw.City, raw.city, details.city, details.City);
  const zipCode = firstString(
    raw.ZipCode,
    raw.zipCode,
    details.zipCode,
    details.ZipCode
  );
  const streetAddress = firstString(
    raw.StreetAddress,
    raw.streetAddress,
    details.streetAddress,
    details.StreetAddress
  );
  const iban = firstString(raw.IBAN, raw.iban, details.iban, details.IBAN);
  const supportEmail = firstString(
    raw.SupportEmail,
    raw.supportEmail,
    details.supportEmail,
    details.SupportEmail
  );

  const country = firstString(
    raw.Country,
    raw.country,
    details.country,
    details.Country,
    raw.StripeConnectDefaultCountry,
    details.StripeConnectDefaultCountry
  );

  const logoUrl = logoUrlFromRaw(raw, details);
  const poaUrl = poaUrlFromDetails(details);

  const logoFileName = firstString(
    raw.LogoFileName,
    raw.logoFileName,
    logoUrl?.startsWith('http') ? fileNameFromUrl(logoUrl) : undefined
  );

  const poaFileName = firstString(
    raw.PoaFileName,
    raw.poaFileName,
    poaUrl ? fileNameFromUrl(poaUrl) : undefined
  );

  return {
    TenantId: tenantId,
    TenantName: tenantName,
    TenantCode:
      raw.TenantCode != null || raw.tenantCode != null
        ? String(raw.TenantCode ?? raw.tenantCode).trim() || undefined
        : undefined,
    Email: email,
    City: city,
    ZipCode: zipCode,
    StreetAddress: streetAddress,
    IBAN: iban,
    SupportEmail: supportEmail,
    Country: country || undefined,
    PlanAmount: toOptionalNumber(raw.PlanAmount ?? raw.planAmount),
    KycStatus: toKycStatus(
      raw.KycStatus ??
        raw.kycStatus ??
        raw.stripeKYCStatus ??
        raw.StripeKYCStatus
    ),
    RevenueConfiguration: String(
      raw.RevenueConfiguration ?? raw.revenueConfiguration ?? '—'
    ),
    CommissionUnder50: toOptionalNumber(
      raw.CommissionUnder50 ?? raw.commissionUnder50
    ),
    Commission50To150: toOptionalNumber(
      raw.Commission50To150 ?? raw.commission50To150
    ),
    CommissionOver150: toOptionalNumber(
      raw.CommissionOver150 ?? raw.commissionOver150
    ),
    BexioTemplateSlug: (() => {
      const v = raw.bexioTemplateSlug ?? raw.BexioTemplateSlug;
      if (v == null || v === '') return undefined;
      return String(v).trim() || undefined;
    })(),
    LogoUrl: logoUrl,
    LogoFileName: logoFileName || undefined,
    PoaUrl: poaUrl,
    PoaFileName: poaFileName || undefined,
  };
}

/**
 * Normalizes list responses where `data` may be a tenant array, or `{ count, tenants }`,
 * or `{ Tenants }` (PascalCase).
 */
function num(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return undefined;
}

/**
 * Reads total row count for pagination. Prefer explicit total* fields; avoid treating
 * `count` as total when it matches page size (same as current row count).
 */
function readListTotalCount(
  p: Record<string, unknown>,
  rowsInPage: number
): number | undefined {
  const explicit =
    num(p.totalCount) ??
    num(p.TotalCount) ??
    num(p.totalRecords) ??
    num(p.TotalRecords) ??
    num(p.recordCount) ??
    num(p.RecordCount) ??
    num(p.total) ??
    num(p.Total);

  if (explicit !== undefined) return explicit;

  const pag = p.pagination ?? p.Pagination;
  if (pag && typeof pag === 'object') {
    const pg = pag as Record<string, unknown>;
    const fromPag =
      num(pg.totalCount) ??
      num(pg.TotalCount) ??
      num(pg.total) ??
      num(pg.totalRecords);
    if (fromPag !== undefined) return fromPag;
  }

  /** Some APIs reuse `count` for page size; only trust it as total when > rows in page. */
  const c = num(p.count) ?? num(p.Count);
  if (c !== undefined && rowsInPage > 0 && c > rowsInPage) {
    return c;
  }

  return undefined;
}

export function normalizeTenantsListBody(body: unknown): TenantsListResponse {
  if (!body || typeof body !== 'object') {
    return { message: '', data: { count: 0, tenants: [] } };
  }
  const b = body as Record<string, unknown>;
  const message = String(b.message ?? b.Message ?? '');
  const payload = b.data;

  let rows: unknown[] = [];

  if (Array.isArray(payload)) {
    rows = payload;
  } else if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (Array.isArray(p.tenants)) {
      rows = p.tenants;
    } else if (Array.isArray(p.Tenants)) {
      rows = p.Tenants;
    } else if (Array.isArray(p.items)) {
      rows = p.items;
    }
  }

  const rowCount = rows.length;

  let countFromApi: number | undefined;
  if (payload && typeof payload === 'object') {
    countFromApi = readListTotalCount(
      payload as Record<string, unknown>,
      rowCount
    );
  }
  if (countFromApi === undefined) {
    countFromApi = readListTotalCount(b, rowCount);
  }

  const tenants = rows
    .filter(
      (x): x is Record<string, unknown> =>
        x != null && typeof x === 'object' && !Array.isArray(x)
    )
    .map(mapTenantFromApi);

  const count = countFromApi ?? tenants.length;

  return {
    message,
    data: {
      count,
      tenants,
    },
  };
}

/** Unwraps `{ message, data: tenant }` or a raw tenant object from GET/PATCH/POST. */
export function unwrapTenantResponseBody(body: unknown): Tenant {
  if (!body || typeof body !== 'object') {
    return mapTenantFromApi({});
  }
  const b = body as Record<string, unknown>;
  const inner = b.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return mapTenantFromApi(inner as Record<string, unknown>);
  }
  return mapTenantFromApi(b);
}

function mapDeleteEligibilityFromApi(
  raw: Record<string, unknown>
): TenantDeleteEligibility {
  const customerCountRaw = raw.customerCount ?? raw.CustomerCount;
  const customerCount =
    typeof customerCountRaw === 'number'
      ? customerCountRaw
      : Number(customerCountRaw ?? 0);

  return {
    canDelete: Boolean(raw.canDelete ?? raw.CanDelete),
    customerCount: Number.isFinite(customerCount) ? customerCount : 0,
    message: String(raw.message ?? raw.Message ?? '').trim(),
  };
}

/** GET delete-eligibility — `{ message, data: { canDelete, customerCount, message } }`. */
export function unwrapDeleteEligibilityBody(
  body: unknown
): TenantDeleteEligibility {
  if (!body || typeof body !== 'object') {
    return { canDelete: false, customerCount: 0, message: '' };
  }
  const b = body as Record<string, unknown>;
  const inner = b.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return mapDeleteEligibilityFromApi(inner as Record<string, unknown>);
  }
  return mapDeleteEligibilityFromApi(b);
}
